'use client';

import { clsx, usePageLayout } from '@libs/common-next';
import { usePathname } from 'next/navigation';
import { Section as SparkySection } from '@petsmart-ui/sparky';
import { trackContentfulClick } from '@libs/analytics';
import { propsImagesItems } from '../../utils/propsImagesItems';
import type { ProductCarouselProps } from './types';
import { COLOR_TABLE } from '../../constants';
import PromoCard from '../PromoCard';
import { HeaderCtaTracker } from '../../utils/HeaderCtaTracker';

export function Section({
    className,
    headline,
    description,
    linkAction,
    variant = 'default',
    card,
    isH1,
    linkActionColor = 'Blue',
    backgroundColor,
    textColor = 'Dark Grey',
    children,
    ...other
}: Omit<ProductCarouselProps, 'products'>) {
    const pathname = usePathname();
    const { flags } = usePageLayout();
    const { redesignedComponentsEnabled } = flags;

    const restyledVariants = ['default', 'storytelling', 'campaign'];
    const isRestyled = restyledVariants.includes(variant) && Boolean(redesignedComponentsEnabled);

    const componentClassName = clsx(
        'product-carousel',
        `product-carousel--${variant}`,
        isRestyled && `product-carousel--restyled`,
        className
    );

    const { link, actionText } = linkAction || {};
    const cardDescription = card?.bodyText ?? card?.description;
    return (
        <SparkySection
            data-testid="product-carousel"
            title={headline}
            description={description}
            headingTagName={isH1 ? 'h1' : 'h2'}
            {...(isRestyled && {
                align: 'center',
                titleAfterLocation: 'below-description',
            })}
            titleAfter={
                link?.webUrl && actionText ? (
                    <HeaderCtaTracker
                        textLink={linkAction}
                        linkTextColor={linkActionColor}
                        header={headline ?? ''}
                        promoVariant={`product-carousel - ${variant}`}
                        promoType={`product-carousel--${variant}`}
                    />
                ) : null
            }
            className={componentClassName}
            {...(variant === 'campaign' && {
                style: {
                    backgroundColor,
                    color: COLOR_TABLE[textColor],
                },
            })}
            {...other}
        >
            {variant === 'storytelling' && card && (
                <div className="product-carousel__card">
                    <PromoCard
                        data-testid="product-carousel-card"
                        header={card.header}
                        description={cardDescription}
                        imageItems={propsImagesItems(card.image)}
                        imgAlt={card.image.webAltText}
                        link={card.link}
                        onClick={() => {
                            trackContentfulClick({
                                linkText: `${card.header} ${cardDescription}`,
                                linkUrl: card.link?.webUrl ?? '',
                                promoTitle: headline ?? '',
                                promoVariant: 'product carousel - storytelling',
                                promoDesc: 'product-carousel__card',
                                promoType: 'product carousel - storytelling',
                                promoOrigin: pathname,
                            });
                        }}
                    />
                </div>
            )}

            {children}
        </SparkySection>
    );
}
